import React from 'react'
import { hasPermission } from '@deseretbook/cms'
import { DeseretBookCMS } from '../../dbcom'

/**
 * Resolves CMS links.
 *
 * @param {object} doc - document to come up with a link for
 * @returns {string} resolved link
 */
export const linkResolver = () => '/'

/**
 * Formats an image's `srcset` for use with the `formatImageProps` function.
 *
 * @param {object} image - Image to format srcset for.
 * @param {array<string>} [sizes] - Names of different sizes to look for and to use
 * @returns {string} Formatted srcset for image.
 */
const formatSrcSet = (image = {}, sizes = ['small', 'medium', 'large']) => {
  const sources = Object.entries(image)
    .filter(([key, value]) => sizes.indexOf(key) > -1 && value.url)
    .map((entry) => {
      const value = entry[1]

      if (value.dimensions && value.dimensions.width) {
        return `${value.url} ${value.dimensions.width}w`
      }

      return value.url
    })

  sources.push(image.dimensions && image.dimensions.width
    ? `${image.url} ${image.dimensions.width}w`
    : image.url)

  return sources.join(', ')
}


/**
 * Takes image info from the CMS and builds image props for it.
 *
 * @param {object} [image={}] Image to format props based on
 * @returns {object} Props to apply to an `<Image />`
 */
export const formatImageProps = (image = {}) => {
  const props = {}

  if (image.alt) {
    props.alt = image.alt
  }

  if (image.dimensions && image.dimensions.width) {
    props.imageWidth = image.dimensions.width
  }

  if (image.dimensions && image.dimensions.height) {
    props.imageHeight = image.dimensions.height
  }

  props.srcSet = formatSrcSet(image)
  props.src = image.url

  return props
}

/**
 * Custom hook that retrieves a MediaQuery document from the CMS.
 *
 * @param {object} document - Media Query document
 * @param {string} document.id - document id
 * @param {object} [cms=DeseretBookCMS] - CMS to find document in
 * @returns {object} Info about the document
 */
export const useMediaQuery = (document) => {
  const [mediaQuery, setMediaQuery] = React.useState({})
  const [isLoading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  /**
   * Gets the document from the CMS.
   */
  const getDocument = async () => {
    if (!document || !document.id) {
      return
    }

    try {
      setLoading(true)
      const mediaQueryDocument = await DeseretBookCMS.mediaQueries().getByID(document.id)

      setMediaQuery(mediaQueryDocument)
    } catch (caughtError) {
      setError(caughtError)
    }

    setLoading(false)
  }

  React.useEffect(() => {
    getDocument()
  }, [document])

  return { mediaQuery, isLoading, error }
}

/**
 * Custom hook that retrieves a Theme document from the CMS.
 *
 * @param {object} document - Info about theme
 * @param {string} document.id - document id
 * @param {object} [cms=DeseretBookCMS] - CMS to find document in
 * @returns {object} Info about the document
 */
export const useTheme = (document) => {
  const [theme, setTheme] = React.useState({})
  const [isLoading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  /**
   * Gets the document from the CMS.
   */
  const getDocument = async () => {
    if (!document || !document.id) {
      return
    }

    try {
      setLoading(true)
      const themeDocument = await DeseretBookCMS.themes().getByID(document.id)

      setTheme(themeDocument)
    } catch (caughtError) {
      setError(caughtError)
    }

    setLoading(false)
  }

  React.useEffect(() => {
    getDocument()
  }, [document])

  return { theme, isLoading, error }
}

/**
 * Custom hook that retrieves a UserPermission document from the CMS.
 *
 * @param {object} document - Permission document details
 * @param {string} document.id - Id of permission document
 * @param {object} [user] - Current user object
 * @returns {object} Info about if the user has permission or not
 */
export const useUserPermission = (document, user) => {
  const [userHasPermission, setPermission] = React.useState(false)
  const [isLoading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  /**
   * Gets the document from the CMS.
   */
  const getDocument = async () => {
    if (!document || !document.id) {
      setPermission(true)

      return
    }

    try {
      setLoading(true)

      const permissionDocument = await DeseretBookCMS.userPermissions().getByID(document.id)

      setPermission(hasPermission(permissionDocument, user))
    } catch (caughtError) {
      setError(caughtError)
    }

    setLoading(false)
  }

  React.useEffect(() => {
    getDocument()
  }, [document])

  return { hasPermission: userHasPermission, isLoading, error }
}
